import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import RoutingStrip from "../../components/RoutingStrip.jsx";
import CardList from "../ui/CardList.jsx";
import {
  formatDateCentral,
  formatDateTimeCentralShort,
  getProductionProgress,
  isStationDone,
  loadPartProgress,
  loadTodayLists,
  searchParts,
} from "../../features/progress/progressData";
import { stepState } from "./routing";
import "../mobile.css";

// The phone Progress page (plan Phase 2): "Where is this part?" (search, then the part's open jobs,
// furthest along first, with their stations) and today's lists (shipped, ready to ship, finishing).
// Read-only, as on the desktop. Layout only: features/progress/progressData.js is the desktop page's
// own code.

function StationSteps({ job }) {
  const steps = (job.stations || []).map((s) => ({
    key: s.station,
    label: s.station,
    state: isStationDone(s.status) ? "done" : stepState(s.status),
    sub: s.status === "Completed" || s.status === "Shipped"
      ? (s.end_time ? formatDateTimeCentralShort(s.end_time) : undefined)
      : (s.scheduled_date ? formatDateCentral(s.scheduled_date) : undefined),
  }));
  return <RoutingStrip steps={steps} ariaLabel={`Stations for job ${job.master_job_num ?? job.job_id}`} />;
}

export function PartJobCard({ job }) {
  const { currentIndex, total } = getProductionProgress(job.stations);
  return (
    <div className="jq-card" data-testid={`progress-job-${job.job_id}`}>
      <div className="d-flex align-items-center gap-2">
        <Link to={`/jobs/${job.job_id}`} className="fw-bold flex-grow-1">Job #{job.master_job_num ?? job.job_id}</Link>
        <span className="jq-tag">{currentIndex} of {total} stations</span>
      </div>
      <div className="jq-muted">
        Ordered {job.quantity_ordered ?? 0} · defective {job.quantity_defected || 0} · expected {job.quantity_expected || 0}
      </div>
      <div className="jq-muted mb-2">
        Estimated completion {job.estimated_completion_date ? formatDateCentral(job.estimated_completion_date) : "not scheduled"}
      </div>
      <StationSteps job={job} />
    </div>
  );
}

function TodayList({ title, rows, empty, testId, render }) {
  return (
    <>
      <h2 className="jq-section-title mt-3">{title}<span className="jq-queue-count">{rows.length}</span></h2>
      <CardList items={rows} getKey={(r) => r.task_id} renderCard={render} empty={empty} testId={testId} />
    </>
  );
}

export default function ProgressScreen() {
  const [today, setToday] = useState(null);
  const [todayError, setTodayError] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [part, setPart] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [partError, setPartError] = useState(null);
  const [partLoading, setPartLoading] = useState(false);
  const debounce = useRef(null);
  const searchSeq = useRef(0);
  const partSeq = useRef(0);

  useEffect(() => {
    let live = true;
    loadTodayLists()
      .then((d) => { if (live) setToday(d); })
      .catch((err) => { if (live) setTodayError(err.message || "Couldn't load today's lists."); });
    return () => { live = false; if (debounce.current) clearTimeout(debounce.current); };
  }, []);

  const onQuery = (value) => {
    setQuery(value);
    setPart(null);
    setJobs(null);
    setPartError(null);
    const mine = ++searchSeq.current; // an older answer must not land after this keystroke
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      const found = await searchParts(value);
      if (mine === searchSeq.current) setResults(Array.isArray(found) ? found : []);
    }, 300);
  };

  const pickPart = useCallback(async (p) => {
    const mine = ++partSeq.current;
    if (debounce.current) clearTimeout(debounce.current); // a search still waiting to fire must not reopen the list
    searchSeq.current += 1; // nor an answer still on its way
    setPart(p);
    setResults([]);
    setJobs(null);
    setPartError(null);
    setPartLoading(true);
    try {
      const data = await loadPartProgress(p.part_id);
      if (mine === partSeq.current) setJobs(data);
    } catch (err) {
      if (mine === partSeq.current) setPartError(err.message);
    } finally {
      if (mine === partSeq.current) setPartLoading(false);
    }
  }, []);

  return (
    <div className="jq-screen" data-testid="phone-progress">
      <h2 className="jq-section-title">Where is this part?</h2>
      <div className="jq-search mb-2">
        <i className="fas fa-search" aria-hidden="true" />
        <input
          type="search"
          inputMode="search"
          enterKeyHint="search"
          className="form-control"
          placeholder="Part number or description"
          aria-label="Part number or description"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
        />
      </div>
      {results.length > 0 && (
        <div className="mb-2" data-testid="progress-part-results">
          {results.map((p) => (
            <button key={p.part_id} type="button" className="jq-card jq-card-button" onClick={() => pickPart(p)}>
              <span className="fw-bold">{p.product_id}</span> <span className="jq-muted">{p.description}</span>
              <i className="fas fa-chevron-right jq-card-chevron" aria-hidden="true" />
            </button>
          ))}
        </div>
      )}
      {part && (
        <div data-testid="progress-part">
          <div className="fw-semibold mb-2">{part.product_id} <span className="jq-muted">{part.description}</span></div>
          {partLoading && <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /></div>}
          {partError && <div className="jq-note jq-note-danger" role="alert">{partError}</div>}
          {jobs && jobs.length === 0 && <div className="jq-list-state">No pending or in-progress jobs for this part.</div>}
          {jobs && jobs.map((j) => <PartJobCard key={j.job_id} job={j} />)}
        </div>
      )}

      {todayError && <div className="jq-note jq-note-danger mt-3" role="alert">{todayError}</div>}
      {!today && !todayError && (
        <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /></div>
      )}
      {today && today.failed && today.failed.length > 0 && (
        <div className="jq-note jq-note-danger mt-3" role="alert" data-testid="progress-failed">
          Couldn't load {today.failed.map((f) => ({ finishing: "finishing today", shipped: "shipped today", ready: "ready to ship" }[f] || f)).join(", ")}.
        </div>
      )}
      {today && (
        <>
          <TodayList
            title="Shipped today"
            rows={today.shipped}
            empty="No parts shipped today."
            testId="progress-shipped"
            render={(l) => (
              <div>
                <div><span className="fw-bold">{l.product_id}</span> <span className="jq-muted">{l.description}</span></div>
                <div className="jq-muted">Job {l.master_job_num} · shipped {l.quantity_shipped} · {formatDateCentral(l.shipped_date)}</div>
              </div>
            )}
          />
          <TodayList
            title="Ready to ship"
            rows={today.ready}
            empty="No parts ready to ship today."
            testId="progress-ready"
            render={(l) => (
              <div>
                <div><span className="fw-bold">{l.product_id}</span> <span className="jq-muted">{l.description}</span></div>
                <div className="jq-muted">
                  Job {l.master_job_num} · {l.quantity_ordered} ordered / {l.quantity_shipped || 0} shipped · {l.scheduled_date ? formatDateCentral(l.scheduled_date) : "Today"}
                </div>
              </div>
            )}
          />
          <TodayList
            title="Finishing today"
            rows={today.finishing}
            empty="No parts scheduled to finish today."
            testId="progress-finishing"
            render={(l) => (
              <div>
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-bold flex-grow-1">{l.product_id}</span>
                  <span className={`jq-status ${/in\s*progress/i.test(l.status || "") ? "jq-status-paused" : "jq-status-pending"}`}>{l.status || "Finishing"}</span>
                </div>
                <div className="jq-muted">{l.description}</div>
                <div className="jq-muted">Job {l.master_job_num} · {l.quantity_ordered} ordered / {l.quantity_shipped || 0} shipped</div>
              </div>
            )}
          />
        </>
      )}
    </div>
  );
}
